/**
 * Cover art library — swap the cover on a quote so each proposal looks bespoke.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Check, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/App";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const CoverPickerDialog = ({ quote, token, onClose, onApplied }) => {
  const [lib, setLib] = useState({ covers: [], insets: [] });
  const [cover, setCover] = useState(quote.cover_slug || "");
  const [inset, setInset] = useState("");
  const [headline, setHeadline] = useState(quote.cover_headline || "");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/admin/quote-covers`, { headers })
      .then((r) => setLib(r.data))
      .catch(() => toast.error("Could not load the cover library"))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/capital-quotes/${quote.id}/cover`, {
        ...(cover ? { cover_slug: cover } : {}),
        ...(inset ? { inset_slug: inset } : {}),
        cover_headline: headline,
      }, { headers });
      toast.success("Cover applied", { description: "Open Preview or the PDF to see it." });
      onApplied?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply that cover");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-start justify-center p-3 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="cover-picker-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-6">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] font-bold text-blue-700">{quote.quote_number}</p>
            <h3 className="text-base font-bold text-slate-900 inline-flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-blue-600" /> Cover art
            </h3>
            <p className="text-[12px] text-slate-500">
              Pick the backdrop that matches the project. Your logo, the customer's logo and the
              quote details are laid over it automatically.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"
            data-testid="cover-picker-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-5">
          {loading && (
            <p className="text-center text-slate-400 py-8">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading the library…
            </p>
          )}

          {!loading && (
            <>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
                  Cover headline
                </label>
                <Input value={headline} onChange={(e) => setHeadline(e.target.value)}
                  placeholder="Progressive Water Softening System" data-testid="cover-headline" />
              </div>

              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Backdrop — {lib.covers.length} in the library
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {lib.covers.map((c) => (
                    <button key={c.slug} onClick={() => setCover(c.slug)}
                      className={`text-left rounded-lg overflow-hidden border-2 transition-colors ${
                        cover === c.slug ? "border-blue-600" : "border-slate-200 hover:border-slate-400"}`}
                      data-testid={`cover-option-${c.slug}`}>
                      <div className="relative">
                        <img src={`${BACKEND}${c.thumb_url}`} alt={c.name}
                          className="w-full h-28 object-cover" />
                        {cover === c.slug && (
                          <span className="absolute top-1.5 right-1.5 bg-blue-600 text-white rounded-full p-1">
                            <Check className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                      <div className="p-2">
                        <p className="text-[12px] font-bold text-slate-900 leading-tight">{c.name}</p>
                        <p className="text-[10.5px] text-slate-500 mt-0.5 leading-snug">{c.note}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Inset image <span className="normal-case font-normal text-slate-400">— the framed photo on the cover</span>
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {lib.insets.map((i) => (
                    <button key={i.slug} onClick={() => setInset(i.slug)}
                      className={`text-left rounded-lg overflow-hidden border-2 transition-colors ${
                        inset === i.slug ? "border-blue-600" : "border-slate-200 hover:border-slate-400"}`}
                      data-testid={`inset-option-${i.slug}`}>
                      <img src={`${BACKEND}${i.image_url}`} alt={i.name}
                        className="w-full h-24 object-cover" />
                      <p className="text-[12px] font-bold text-slate-900 p-2 leading-tight">{i.name}</p>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-between gap-2">
          <button className="text-[12px] text-slate-500 underline hover:text-slate-800"
            onClick={() => window.open(`${BACKEND}/api/capital-quotes/${quote.id}/preview`, "_blank", "noopener")}
            data-testid="cover-preview-current">
            Preview the current cover
          </button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={apply} disabled={saving || loading}
              className="bg-blue-600 hover:bg-blue-700" data-testid="cover-apply">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} Apply cover
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoverPickerDialog;
